import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsCorporateAccount, useTheme } from '@nbx/frontend-helpers/subscribe';
import { useRemoteFeatureFlag } from '@nbx/config';
import isLinkActive from 'helpers/isLinkActive';
import { useIsTablet } from 'helpers/hooks';
import { Context } from 'state/store';
import { setOverlay, setNavRef } from 'state/creators';
import OVERLAYS from 'constants/overlays';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

const NavigationPortal = props => {
  return ReactDOM.createPortal(<Navigation {...props} />, document.getElementById('navigation_hook'));
};

const Navigation = props => {
  const { t } = useTranslation();
  const [state, dispatch] = React.useContext(Context);
  const theme = useTheme();
  const location = useLocation();
  const isTablet = useIsTablet();
  const navRef = React.useRef(null);
  const isCorporateAccount = useIsCorporateAccount();
  const clearOverlay = React.useCallback(
    event => {
      if (event.target === document) dispatch(setOverlay(null));
    },
    [dispatch]
  );
  const openOTC = () => dispatch(setOverlay(OVERLAYS.otc));
  const navOpen = state.overlay === OVERLAYS.nav;

  const [isTokenizationEnabled] = useRemoteFeatureFlag('funds_tokenization_feature_flag');

  // The scroll event listener is not getting torn down like it should
  React.useEffect(() => {
    document.removeEventListener('scroll', clearOverlay);
    if (navOpen) document.addEventListener('scroll', clearOverlay);
    return () => document.removeEventListener('scroll', clearOverlay);
  }, [navOpen, clearOverlay]);

  React.useEffect(() => {
    const navHook = document.getElementById('navigation_hook');
    if (isTablet || props.isTradingPage) {
      navHook.classList.add('slide-over');
    } else {
      navHook.classList.remove('slide-over');
    }
  }, [isTablet, props.isTradingPage]);

  // Set ref
  React.useEffect(() => {
    dispatch(setNavRef(navRef.current));
    return () => dispatch(setNavRef(null));
  }, [dispatch]);

  return (
    <nbx-theme-provider theme={theme} flex>
      <div
        className={classnames([
          styles.navigation,
          {
            [styles.open]: navOpen,
            [styles.slideOverNav]: isTablet || props.isTradingPage
          }
        ])}
        ref={navRef}>
        <nbx-button-text
          to={microapps?.routes?.dashboards?.path}
          onClick={clearOverlay}
          navigation-active={isLinkActive(
            location,
            window.GET_LOCALE_PATHS(microapps?.routes?.dashboards?.path)
          )}
          navigation>
          {t('header_link_dashboard')}
        </nbx-button-text>

        <nbx-button-text
          to={microapps?.routes?.nft?.path}
          onClick={clearOverlay}
          navigation-active={isLinkActive(location, [
            ...window.GET_LOCALE_PATHS(microapps?.routes?.nft?.path)
          ])}
          navigation>
          {t('header_link_nft')}
        </nbx-button-text>

        <nbx-button-text
          to={microapps?.routes?.markets?.path}
          onClick={clearOverlay}
          navigation-active={isLinkActive(location, [
            ...window.GET_LOCALE_PATHS(microapps?.routes?.markets?.path),
            ...window.GET_LOCALE_PATHS(microapps?.routes?.trading?.path)
          ])}
          navigation>
          {t('header_link_markets')}
        </nbx-button-text>

        <nbx-button-text
          to={microapps?.routes?.orders?.path}
          onClick={clearOverlay}
          navigation-active={isLinkActive(location, window.GET_LOCALE_PATHS(microapps?.routes?.orders?.path))}
          navigation>
          {t('header_link_orders')}
        </nbx-button-text>

        <nbx-button-text
          to={microapps?.routes?.funds?.path}
          onClick={clearOverlay}
          navigation-active={isLinkActive(location, window.GET_LOCALE_PATHS(microapps?.routes?.funds?.path))}
          navigation>
          {t('header_link_funds')}
        </nbx-button-text>

        {isTokenizationEnabled && (
          <nbx-button-text
            to={microapps?.routes?.fundsTokenization?.path}
            onClick={clearOverlay}
            navigation-active={isLinkActive(
              location,
              window.GET_LOCALE_PATHS(microapps?.routes?.fundsTokenization?.path)
            )}
            navigation>
            {t('funds_tokenization')}
          </nbx-button-text>
        )}

        <nbx-button-text
          to={microapps?.routes?.staking?.path}
          onClick={clearOverlay}
          navigation-active={isLinkActive(
            location,
            window.GET_LOCALE_PATHS(microapps?.routes?.staking?.path)
          )}
          navigation>
          {t('staking_staking')}
        </nbx-button-text>

        <nbx-button-text
          to={microapps?.routes?.card?.path}
          onClick={clearOverlay}
          navigation-active={isLinkActive(location, window.GET_LOCALE_PATHS(microapps?.routes?.card?.path))}
          navigation>
          {t('common_card')}
        </nbx-button-text>

        {!isCorporateAccount && (
          <nbx-button-text
            to={microapps?.routes?.referral?.path}
            onClick={clearOverlay}
            navigation-active={isLinkActive(
              location,
              window.GET_LOCALE_PATHS(microapps?.routes?.referral?.path)
            )}
            navigation>
            {t('header_link_referral')}
          </nbx-button-text>
        )}

        <nbx-button-text navigation onClick={openOTC}>
          {t('header_link_otc')}
        </nbx-button-text>

        <nbx-button-text
          to={microapps?.routes?.accountGeneral?.path}
          onClick={clearOverlay}
          navigation-active={isLinkActive(
            location,
            window.GET_LOCALE_PATHS(microapps?.routes?.accountGeneral?.path)
          )}
          navigation>
          {t('header_account')}
        </nbx-button-text>

        <nbx-button-text onClick={clearOverlay} navigation href="https://nbxsupport.zendesk.com">
          {t('common_support')}
        </nbx-button-text>
      </div>
    </nbx-theme-provider>
  );
};

Navigation.propTypes = {
  isTradingPage: PropTypes.bool
};

Navigation.defaultProps = {
  isTradingPage: false
};

export default NavigationPortal;
